import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Link,
  CircularProgress,
  AppBar,
  Toolbar,
  useMediaQuery,
  Select,
  MenuItem,
  Menu,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import bg from "../../assets/femme-background.jpg";
import logo from "../../assets/logo-full.png";

// petits drapeaux PNG (discrets)
import frFlag from "../../assets/fr-flag.png";
import ukFlag from "../../assets/uk-flag.png";
import plFlag from "../../assets/pl-flag.png";

const BRAND = "#449F2D";
const BRAND_HOVER = "#388624";

const flagIcons = {
  fr: frFlag,
  en: ukFlag,
  pl: plFlag,
};

export default function Register() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("pages/authentification");

  const [ident, setIdent] = useState("");
  const [loading, setLoading] = useState(false);
  const canSubmit = !!ident && !loading;

  // état pour le menu de langues mobile
  const [flagAnchor, setFlagAnchor] = useState(null);
  const openFlags = Boolean(flagAnchor);
  const changeLang = (lng) => {
    i18n.changeLanguage(lng);
    setFlagAnchor(null);
  };

  // même seuil que Login
  const isNarrow = useMediaQuery("(max-width:800px)");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      // TODO: appel API de vérif identifiant
      await new Promise((r) => setTimeout(r, 700));
      navigate(`/inscription/verification?ident=${encodeURIComponent(ident)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", position: "relative", overflow: "hidden", bgcolor: isNarrow ? "#fff" : "transparent" }}>
      {/* Sélecteur de langue — bottom-center en mobile (Button+Menu), top-right en desktop (Select) */}
      {isNarrow ? (
        <Box
          sx={{
            position: "fixed",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1300,
          }}
        >
          <Button
            onClick={(e) => setFlagAnchor(e.currentTarget)}
            variant="outlined"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderColor: "#e6e6e6",
              color: "#449F2D",
              backgroundColor: "rgba(255,255,255,0.95)",
              borderRadius: "999px",
              px: 1.25,
              py: 0.25,
              minWidth: 82,
              textTransform: "none",
              fontWeight: "bold",
              touchAction: "manipulation",
            }}
          >
            <Box
              component="img"
              src={flagIcons[i18n.language] || frFlag}
              alt="lang"
              sx={{ width: 24, height: 16, objectFit: "cover" }}
            />
            {String(i18n.language || "fr").toUpperCase()}
          </Button>

          <Menu
            anchorEl={flagAnchor}
            open={openFlags}
            onClose={() => setFlagAnchor(null)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            transformOrigin={{ vertical: "bottom", horizontal: "center" }}
            keepMounted
          >
            <MenuItem onClick={() => changeLang("fr")}>
              <img src={frFlag} alt="FR" width="22" height="14" style={{ marginRight: 8 }} />
              Français
            </MenuItem>
            <MenuItem onClick={() => changeLang("en")}>
              <img src={ukFlag} alt="EN" width="22" height="14" style={{ marginRight: 8 }} />
              English
            </MenuItem>
            <MenuItem onClick={() => changeLang("pl")}>
              <img src={plFlag} alt="PL" width="22" height="14" style={{ marginRight: 8 }} />
              Polski
            </MenuItem>
          </Menu>
        </Box>
      ) : (
        <Box sx={{ position: "fixed", top: 16, right: 16, zIndex: 1200 }}>
          <Select
            value={i18n.language || "fr"}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
            variant="standard"
            disableUnderline
            sx={{
              display: "flex",
              alignItems: "center",
              fontWeight: "bold",
              color: "#449F2D",
              fontSize: "0.95rem",
              minWidth: 82,
              px: 1,
              "& .MuiSelect-icon": { color: "#449F2D", right: 6 },
              backgroundColor: "rgba(255,255,255,0.9)",
              borderRadius: "999px",
              border: "1px solid #e6e6e6",
            }}
            renderValue={(val) => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box component="img" src={flagIcons[val]} alt={val} sx={{ width: 24, height: 16, objectFit: "cover" }} />
                <span style={{ lineHeight: 1 }}>{String(val).toUpperCase()}</span>
              </Box>
            )}
          >
            <MenuItem value="fr">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img src={frFlag} alt="FR" width="22" height="14" /> Français
              </Box>
            </MenuItem>
            <MenuItem value="en">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img src={ukFlag} alt="EN" width="22" height="14" /> English
              </Box>
            </MenuItem>
            <MenuItem value="pl">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img src={plFlag} alt="PL" width="22" height="14" /> Polski
              </Box>
            </MenuItem>
          </Select>
        </Box>
      )}

      {/* AppBar mobile (fond blanc + logo centré) */}
      {isNarrow && (
        <AppBar position="fixed" elevation={0} sx={{ backgroundColor: "#fff", color: "#111", borderBottom: "1px solid #eee" }}>
          <Toolbar sx={{ minHeight: 64, display: "flex", justifyContent: "center" }}>
            <Link href="/garanties" underline="none" sx={{ display: "inline-block" }}>
              <Box component="img" src={logo} alt="Logo" sx={{ height: 90 }} />
            </Link>
          </Toolbar>
        </AppBar>
      )}

      {/* Background desktop uniquement */}
      {!isNarrow && (
        <>
          <Box aria-hidden sx={{
            position: "fixed", inset: 0, backgroundImage: `url(${bg})`,
            backgroundSize: "cover", backgroundPosition: "center",
            filter: "blur(1px)", transform: "scale(1.02)"
          }} />
          <Box aria-hidden sx={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.18)" }} />
        </>
      )}

      {/* Conteneur : mobile centré, desktop décalé depuis la droite */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: isNarrow ? "center" : "flex-end",
          px: 2,
          pt: isNarrow ? "84px" : 2,     // espace sous AppBar en mobile
          pr: isNarrow ? 2 : "20vw",     // décalage desktop identique à Login
        }}
      >
        <Paper
          elevation={0}
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: { xs: "92vw", sm: "100%" }, // même largeur fluide que Login
            maxWidth: 420,
            mx: { xs: "auto", sm: "auto", md: 0 }, // centré < 800px, à droite en desktop
            px: { xs: 3, sm: 5 },
            py: { xs: 3.5, sm: 5 },
            borderRadius: 3,
            backgroundColor: isNarrow ? "#fff" : "rgba(255,255,255,0.96)",
            border: "1px solid #e6e6e6",
            backdropFilter: isNarrow ? "none" : "saturate(120%) blur(6px)",
          }}
        >
          <Stack spacing={2.25}>
            {/* Logo desktop */}
            {!isNarrow && (
              <Box sx={{ display: "grid", placeItems: "center", mt: 0.5 }}>
                <Link href="/garanties" underline="none" sx={{ display: "inline-block" }}>
                  <Box component="img" src={logo} alt="Logo" sx={{ height: { xs: 70, md: 90 } }} />
                </Link>
              </Box>
            )}

            {/* Texte d’intro */}
            <Typography sx={{ textAlign: "left", color: "#4b5563", lineHeight: 1.35, fontSize: 15.5 }}>
              {t("register.intro")}
            </Typography>

            {/* Identifiant */}
            <TextField
              placeholder={t("register.identifierPlaceholder")}
              value={ident}
              onChange={(e) => setIdent(e.target.value)}
              fullWidth
              size="medium"
              helperText={
                <Link href="/aide/identifiant" underline="hover" sx={{ color: BRAND, fontWeight: 500, fontSize: 12 }}>
                  {t("register.findIdentifier")}
                </Link>
              }
              FormHelperTextProps={{ sx: { mt: 0.5 } }}
              InputProps={{
                sx: {
                  borderRadius: 999,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9ca3af" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BRAND },
                  py: 0.25,
                },
              }}
            />

            {/* Boutons bas */}
            <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  borderColor: BRAND,
                  color: BRAND,
                  "&:hover": { borderColor: BRAND_HOVER, backgroundColor: "rgba(68,159,45,0.06)" },
                  borderRadius: 999,
                  px: 3, py: 1, textTransform: "none", fontWeight: 800, minWidth: 120,
                }}
              >
                {t("register.back")}
              </Button>

              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={!canSubmit}
                sx={{
                  bgcolor: BRAND, color: "#fff",
                  "&:hover": { bgcolor: BRAND_HOVER, color: "#fff" },
                  "&.Mui-disabled": { color: "#fff", opacity: 0.6 },
                  borderRadius: 999,
                  px: 3, py: 1, textTransform: "none", fontWeight: 800, minWidth: 120,
                }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : t("register.submit")}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}